import axios from "axios";
import { showSubscriptionExpiredDialog } from "../utils/subscriptionExpiredDialog";
import {
  consumeReturnToPath,
  hasAdminStash,
  isImpersonationToken,
  restoreAdminSession,
} from "../utils/impersonation";

// Base URL Configuration — FastAPI backend
const API = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  // 300s, matching nginx (proxy_read_timeout 300) and gunicorn (--timeout
  // 300) exactly. This was 180s while the server allowed 300 — so a long
  // income-tax generation (attachments + masking + LLM) survived the SERVER
  // limits and was then aborted by the CLIENT at three minutes with
  // "timeout of 180000ms exceeded". Whichever link is shortest decides;
  // keep all three the same on purpose. Anything longer than 300s needs the
  // async task path (202 + poll), not a bigger timeout.
  timeout: 300000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Embed-mode token store. Set by `<EmbedRoot />` after reading the JWT
// from `?token=` on iframe mount and cleared on unmount. In-memory only
// per MODULE 11 §3.2 — must NEVER be persisted to localStorage:
// (a) the iframe lives in a partitioned storage context where parent-
//     domain localStorage is unreliable, and
// (b) persisting widens the leak window for the URL-borne token.
// Module-level rather than `window.__embedToken` so we don't rely on a
// global side-effect for auth state.
let _embedToken = null;
export const setEmbedToken = (jwt) => {
  _embedToken = jwt || null;
};
export const clearEmbedToken = () => {
  _embedToken = null;
};
export const getEmbedToken = () => _embedToken;

// Embed-mode 401 hook. EmbedRoot wires this so a tenant-API 401 inside
// the iframe surfaces as a `postMessage("noticeai.token_expired")` to
// the parent ZOHO frame (per §4.2). Non-embed code paths leave it as a
// no-op and keep their existing logout behaviour.
let _embedUnauthorizedHook = null;
export const setEmbedUnauthorizedHook = (fn) => {
  _embedUnauthorizedHook = typeof fn === "function" ? fn : null;
};

// Request Interceptor — JWT Bearer auth.
// Embed-mode in-memory token wins when set; otherwise fall back to the
// localStorage token used by the standard tenant + admin surfaces.
API.interceptors.request.use(
  (config) => {
    const token = _embedToken || localStorage.getItem("authToken") || "";
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor (Handle global API responses)
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response) {
      const { status } = error.response;

      if (status === 401) {
        // Embed-mode 401: don't touch localStorage (the iframe doesn't
        // own those tokens), don't redirect to /login (we're inside
        // someone else's iframe). Hand off to the parent via the hook.
        if (_embedToken && _embedUnauthorizedHook) {
          _embedUnauthorizedHook(error);
        } else if (
          isImpersonationToken(localStorage.getItem("authToken")) &&
          hasAdminStash()
        ) {
          // Impersonation-shadow token expired (or was rejected) mid-
          // request. Do NOT sign the admin out — restore their original
          // session from the sessionStorage stash and hard-nav back to
          // the returnTo path they came from. Mirrors what the banner's
          // manual "Stop impersonating" flow does; the only difference is
          // trigger (server 401 vs user click).
          //
          // Previously this branch fell through to the sign-out below,
          // so any late 401 (a background fetch after JWT exp, a
          // component-mount refetch after the banner's countdown tick
          // was throttled while the tab was hidden, the banner's own
          // audit ping firing with the dead shadow token) would wipe
          // BOTH tokens and dump the admin on /login — the bug this
          // fix addresses.
          console.warn("Impersonation token expired - restoring admin session.");
          const target = consumeReturnToPath();
          // restoreAdminSession is async — it may mint a fresh admin access
          // token off the stashed refresh token (the stashed access token
          // outlives its 15-minute life during any real impersonation).
          // Navigating BEFORE that completes would boot the admin panel on
          // the dead token and sign the admin out — so chain the hard-nav.
          // Hard-nav rather than react-router navigate() so AuthContext
          // re-hydrates from the (now-restored) localStorage and any
          // half-mounted impersonated-user routes unmount cleanly.
          return restoreAdminSession()
            .catch(() => null)
            .then(() => {
              window.location.href = target;
              return Promise.reject(error);
            });
        } else {
          // Standard 401: clear tokens AND redirect to /login. The
          // redirect is critical — without it the React tree keeps
          // running, AuthContext still believes the user is signed in
          // (its in-memory state hasn't been told), routes stay
          // mounted, and the user sees a cascade of API errors as
          // every component refetches with no token. A hard navigation
          // forces a full reload so AuthContext re-hydrates from the
          // now-empty localStorage and lands the user on /login.
          //
          // BE returns 401 for any of: expired/invalid JWT, deleted
          // user, deleted/soft-deleted org, suspended org — all cases
          // where the user genuinely shouldn't be inside the product.
          // 403 is reserved for "authenticated-but-forbidden" cases
          // (quota limit, missing role, etc.) and is deliberately NOT
          // handled here, otherwise hitting any limit (AI 5/5,
          // clients 30/30, …) would kick the user back to login.
          console.error("Unauthorized! Logging out...");
          localStorage.removeItem("authToken");
          localStorage.removeItem("refreshToken");
          // Guard against redirect loops on the login / forgot /
          // reset-password / signup pages — they may legitimately get
          // 401 (e.g. bad credentials, expired reset token), and
          // navigating back to themselves accomplishes nothing.
          const path = window.location.pathname;
          const isAuthPage =
            path === "/login" ||
            path === "/partner-admin/login" ||
            path === "/signup" ||
            path.startsWith("/forgot-password") ||
            path.startsWith("/reset-password");
          if (!isAuthPage) {
            // Stash the path so the post-login redirect can return
            // the user where they were, if that flow exists.
            try {
              sessionStorage.setItem(
                "post_login_redirect",
                path + window.location.search,
              );
            } catch (_e) {
              /* sessionStorage can be disabled in private mode — ignore */
            }
            window.location.href = "/login";
          }
        }
      }

      // Expired trial / subscription refusing a MUTATING action. The backend
      // gates fetch/generate/add-client endpoints with a structured 403 so an
      // expired org keeps full read access but every action lands here — one
      // interceptor, one dialog, no per-page wiring. Ordinary 403s (quota,
      // role) don't carry these codes and are untouched.
      let expiryHandled = false;
      if (status === 403) {
        const detail = error.response?.data?.detail;
        if (
          detail &&
          (detail.code === "trial_expired" ||
            detail.code === "subscription_expired")
        ) {
          expiryHandled = true;
          showSubscriptionExpiredDialog({
            message: detail.message,
            shopUrl: detail.shop_url,
            code: detail.code,
          });
        }
      }
      // A refusal the dialog just explained to the user is a handled state,
      // not an error — logging it at error level filled the console with red
      // for every blocked click. (The browser's own "Failed to load resource
      // ... 403" line above it is native and cannot be suppressed from JS.)
      if (expiryHandled) {
        console.info("Action blocked: subscription expired (dialog shown).");
      } else {
        console.error("API Error:", {
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          data: error?.response?.data,
        });
      }
    } else if (error?.request) {
      console.error("API Request Error: No response received");
    } else {
      console.error("API Config Error:", error?.message);
    }
    return Promise.reject(error);
  }
);

export default API;
